import React from 'react';
import { Group, Line, Text } from 'react-konva';
import { WALL_RENDER_PROPERTIES, calculateWallLength } from '../dataModels';
import { DoorRenderer } from './DoorRenderer';
import { WindowRenderer } from './WindowRenderer';
import { ElectricityPointRenderer } from './ElectricityPointRenderer';

// Draw everything in meters - scale is applied at parent Group level
export function WallRenderer({ wall, roomCenter }) {
  const startX = wall.startPoint.x;
  const startY = wall.startPoint.y;
  const endX = wall.endPoint.x;
  const endY = wall.endPoint.y;

  const wallLength = calculateWallLength(wall);
  const { strokeColor, fillColor, strokePattern, thickness } =
    WALL_RENDER_PROPERTIES[wall.type];

  // Wall direction
  const dx = endX - startX;
  const dy = endY - startY;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;

  // Perpendiculars (two possible normals)
  const nx1 = -dy / len;
  const ny1 =  dx / len;
  const nx2 =  dy / len;
  const ny2 = -dx / len;

  // Wall midpoint
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  // Choose normal pointing toward room center
  const toCenterX = roomCenter.x - midX;
  const toCenterY = roomCenter.y - midY;

  const dot1 = nx1 * toCenterX + ny1 * toCenterY;
  const dot2 = nx2 * toCenterX + ny2 * toCenterY;

  const nx = dot1 > dot2 ? nx1 : nx2;
  const ny = dot1 > dot2 ? ny1 : ny2;

  // Wall thickness polygon
  const halfT = thickness / 2;
  const perpX = nx * halfT;
  const perpY = ny * halfT;

  const wallPolygon = [
    startX + perpX, startY + perpY,
    endX   + perpX, endY   + perpY,
    endX   - perpX, endY   - perpY,
    startX - perpX, startY - perpY
  ];

  // Label placement (inside the room)
  const labelOffset = 0.3;
  const labelX = midX + nx * labelOffset;
  const labelY = midY + ny * labelOffset;

  return (
    <Group>
      {/* Wall fill */}
      {fillColor && (
        <Line
          points={wallPolygon}
          closed
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={0.01}
          strokeScaleEnabled={false}
          dash={strokePattern}
          listening={false}
        />
      )}

      {/* Wall centerline */}
      <Line
        points={[startX, startY, endX, endY]}
        stroke={strokeColor}
        strokeWidth={thickness}
        strokeScaleEnabled={false}
        dash={strokePattern}
        listening={false}
      />

      {/* Wall length label INSIDE room */}
      {wallLength > 0.5 && (
        <Text
          x={labelX - 0.5}
          y={labelY - 0.05}
          width={1}
          text={`${wallLength.toFixed(2)} m`}
          fontSize={0.10}
          fill="#666"
          align="center"
          listening={false}
        />
      )}

      {/* Doors */}
      {wall.doors?.map(door => (
        <DoorRenderer key={door.id} door={door} wall={wall} roomCenter={roomCenter} />
      ))}

      {/* Windows */}
      {wall.windows?.map(window => (
        <WindowRenderer key={window.id} window={window} wall={wall} />
      ))}

      {/* Electricity points */}
      {wall.electricityPoints?.map(point => (
        <ElectricityPointRenderer key={point.id} point={point} wall={wall} roomCenter={roomCenter} />
      ))}
    </Group>
  );
}